<template>
  <nav
    class="system-switcher"
    :class="{ 'is-dialog-open': dialogOpen }"
    @mouseleave="openKey = null"
    @focusout="onFocusOut"
    @keydown.escape="openKey = null"
  >
    <a class="system-switcher-home" :href="config.home.url" aria-label="Home" @mouseenter="openKey = null">
      <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12.0105 11.8438C12.0105 12.396 11.5628 12.8438 11.0105 12.8438H2.31323C1.76095 12.8438 1.31323 12.396 1.31323 11.8438V6.27734H0.501003C0.035849 6.27734 -0.177264 5.69783 0.177068 5.39647L6.38185 0.119277C6.56861 -0.0395645 6.843 -0.0395563 7.02975 0.119297L13.2337 5.39649C13.588 5.69786 13.3749 6.27734 12.9098 6.27734H12.0105V11.8438Z"
          fill="currentColor"
        />
      </svg>
    </a>

    <div
      v-for="product in products"
      :key="product.key"
      class="system-switcher-item"
      :class="{
        'is-disabled': !product.enabled,
        'is-active': product.active,
        'is-open': product.isMenu && openKey === product.key,
      }"
      @mouseenter="openKey = null"
    >
      <button
        type="button"
        class="system-switcher-trigger"
        :disabled="!product.enabled"
        @click="onProductClick(product, $event)"
      >
        {{ product.label }}
      </button>

      <div v-if="product.isMenu" class="system-switcher-dropdown">
        <a
          v-for="item in product.items"
          :key="item.id"
          class="system-switcher-dropdown-item"
          :class="{ 'is-active': item.id === current }"
          :href="item.url"
        >
          {{ item.label }}
        </a>
      </div>
    </div>

    <div class="help-menu" ref="helpRoot">
      <button type="button" class="help-menu-trigger" :class="{ 'is-open': helpOpen }" @click="toggleHelp">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="8" cy="8" r="7" stroke="currentColor" stroke-width="1.2" />
          <path
            d="M6 6a2.1 2.1 0 1 1 2.9 1.95c-.53.23-.86.72-.86 1.27v.4"
            stroke="currentColor"
            stroke-width="1.2"
            stroke-linecap="round"
          />
          <circle cx="8" cy="11.7" r="0.75" fill="currentColor" />
        </svg>
        HELP
      </button>

      <div v-if="helpOpen" class="help-menu-panel">
        <p class="help-menu-title">Manuals</p>

        <a
          v-for="manual in availableManuals"
          :key="manual.id"
          class="help-menu-manual"
          :href="manual.url"
          :download="manual.fileName || ''"
          target="_blank"
          rel="noopener"
        >
          <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M9.5 1H2.75A1.75 1.75 0 0 0 1 2.75v12.5C1 16.216 1.784 17 2.75 17h10.5A1.75 1.75 0 0 0 15 15.25V6.5L9.5 1Z"
              stroke="currentColor"
              stroke-width="1.2"
              stroke-linejoin="round"
            />
            <path d="M9.5 1v5.5H15" stroke="currentColor" stroke-width="1.2" stroke-linejoin="round" />
          </svg>
          <span class="help-menu-manual-text">
            <span class="help-menu-manual-label">{{ manual.label }}</span>
            <span v-if="manual.description" class="help-menu-manual-description">{{ manual.description }}</span>
          </span>
        </a>

        <button type="button" class="help-menu-contact" @click="openDialog">Contact us</button>
      </div>

      <div v-if="dialogOpen" class="help-dialog-overlay" @mousedown.self="closeDialog">
        <div
          ref="dialogElement"
          class="help-dialog"
          tabindex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="help-dialog-heading"
        >
          <form class="help-dialog-form" @submit.prevent="submit">
            <div class="help-dialog-header">
              <p id="help-dialog-heading" class="help-dialog-heading">Contact us</p>
              <button type="button" class="help-dialog-close" aria-label="Close" @click="closeDialog">
                <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="m.5.5 18 18m0-18-18 18" stroke="currentColor" stroke-width="1.5" />
                </svg>
              </button>
            </div>

            <div class="help-dialog-fields">
              <div class="help-dialog-field">
                <label class="help-dialog-label" for="help-dialog-category">What is this about?</label>
                <div class="help-dialog-select">
                  <select id="help-dialog-category" class="help-dialog-input" v-model="category" :disabled="sending">
                    <option value="" disabled>Select a topic</option>
                    <option v-for="option in config.support.categories" :key="option.value" :value="option.value">
                      {{ option.label }}
                    </option>
                  </select>
                  <svg width="15" height="9" viewBox="0 0 15 9" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="m.5.5 7 7 7-7" stroke="currentColor" stroke-width="1.5" />
                  </svg>
                </div>
              </div>

              <div class="help-dialog-field">
                <label class="help-dialog-label" for="help-dialog-message">Message</label>
                <textarea
                  id="help-dialog-message"
                  class="help-dialog-input help-dialog-textarea"
                  v-model="message"
                  :disabled="sending"
                  placeholder="Describe what happened, or what you would like to see."
                ></textarea>
              </div>

              <p v-if="error" class="help-dialog-status is-error">{{ error }}</p>
              <p v-if="sent" class="help-dialog-status is-success">Thanks, your message has been sent.</p>
            </div>

            <div class="help-dialog-actions">
              <button type="button" class="help-dialog-cancel" :disabled="sending" @click="closeDialog">Cancel</button>
              <button type="submit" class="help-dialog-submit" :disabled="sending">
                {{ sending ? 'Sending…' : 'Send' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';
import axios from 'axios';

const config = {
  home: { url: 'https://auth.quadratura.se/portal' },

  // Fields per product:
  //   key     unique identifier.
  //   label   text shown in the top bar.
  //   prefix  Any accessible resource whose id starts with this becomes a dropdown
  //           entry. e.g. 'stocklist-' matches stocklist-bmw-se, stocklist-test
  //   labels  Display name per resource id
  //   url     Optional fixed destination.
  products: [
    { key: 'tds', label: 'TDS', prefix: 'tds-', url: 'https://tds.quadratura.se' },
    // {
    //   key: 'vehicle-brochure',
    //   label: 'DIGITAL VEHICLE BROCHURES',
    //   prefix: 'vehicle-brochure',
    //   labels: { 'vehicle-brochure': 'DASHBOARD' },
    // },
    {
      key: 'stocklist',
      label: 'STOCKLIST',
      prefix: 'stocklist-',
      labels: {
        'stocklist-bmw-se': 'BMW SE',
        'stocklist-bmw-no': 'BMW NO',
        'stocklist-bmw-dk': 'BMW DK',
        'stocklist-bmw-fi': 'BMW FI',
        'stocklist-bmw-bl': 'BMW BALTIC',
        'stocklist-motorrad-se': 'MOTORRAD SE',
        'stocklist-motorrad-no': 'MOTORRAD NO',
        'stocklist-motorrad-dk': 'MOTORRAD DK',
        'stocklist-motorrad-fi': 'MOTORRAD FI',
        'stocklist-motorrad-lt': 'MOTORRAD LT',
        'stocklist-motorrad-lv': 'MOTORRAD LV',
        'stocklist-motorrad-ee': 'MOTORRAD EE',
        'stocklist-bl-111': 'BL 111',
        'stocklist-dk-111': 'DK 111',
        'stocklist-dev': 'DEV',
        'stocklist-test': 'TEST',
      },
    },
    {
      key: 'delivery-app',
      label: 'DELIVERY APP',
      prefix: 'delivery-app',
      labels: { 'delivery-app': 'DASHBOARD', 'delivery-app-test': 'TEST' },
    },
  ],

  // Downloadable manuals listed under the HELP button.
  //
  // Fields per manual:
  //   id           unique identifier.
  //   label        text shown in the list.
  //   description  optional one-liner under the label.
  //   url          where the file lives. A same-origin path (files in public/),
  //                or an absolute URL to a CDN / documentation host.
  //   fileName     optional name the browser saves it as. Ignored for
  //                cross-origin URLs — those open in a new tab instead.
  //   systems      which products this manual belongs to, matched against the
  //                `current` resource id ('stocklist' matches 'stocklist-bmw-se').
  //                Omit or use '*' to show it in every system.
  //
  // With no manuals matching the current system, HELP opens the contact form
  // directly instead of showing an empty dropdown.
  //
  // TODO: replace the entries below with the real manual URLs.
  manuals: [],

  support: {
    endpoint: '/oauth2/support',
    categories: [
      { value: 'bug', label: 'Report a bug' },
      { value: 'feature', label: 'Feature request' },
      { value: 'question', label: 'Question / how do I…' },
      { value: 'access', label: 'Access or login problem' },
      { value: 'other', label: 'Something else' },
    ],
  },
};

const props = defineProps({
  current: { type: String, default: '' },
});

const access = ref({});
const openKey = ref(null);

const helpRoot = ref(null);
const helpOpen = ref(false);
const dialogOpen = ref(false);
const dialogElement = ref(null);
const category = ref('');
const message = ref('');
const sending = ref(false);
const sent = ref(false);
const error = ref('');

function itemsForProduct(product) {
  const labels = product.labels ?? {};
  return Object.values(access.value)
    .filter((resource) => resource.id.startsWith(product.prefix))
    .map((resource) => ({
      id: resource.id,
      label: labels[resource.id] ?? resource.id,
      url: resource.url,
    }));
}

const products = computed(() =>
  config.products.map((product) => {
    const items = itemsForProduct(product);
    return {
      key: product.key,
      label: product.label,
      url: product.url,
      items,
      enabled: items.length > 0,
      isMenu: !product.url && items.length > 1,
      active: items.some((item) => item.id === props.current),
    };
  })
);

function onFocusOut(event) {
  if (!event.currentTarget.contains(event.relatedTarget)) openKey.value = null;
}

function onProductClick(product, event) {
  if (!product.enabled) return;
  const target = product.url ?? (product.items.length === 1 ? product.items[0].url : null);
  if (target) {
    event.currentTarget.blur();
    window.location.href = target;
    return;
  }
  openKey.value = openKey.value === product.key ? null : product.key;
}

const availableManuals = computed(() =>
  config.manuals.filter((manual) => {
    const systems = manual.systems ?? ['*'];
    return systems.some(
      (system) => system === '*' || props.current.startsWith(system) || system.startsWith(props.current)
    );
  })
);

const canSubmit = computed(() => !sending.value && category.value !== '' && message.value.trim().length > 0);

function toggleHelp() {
  if (!availableManuals.value.length) {
    openDialog();
    return;
  }
  helpOpen.value = !helpOpen.value;
}

function closeHelp() {
  helpOpen.value = false;
}

function openDialog() {
  closeHelp();
  sent.value = false;
  error.value = '';
  dialogOpen.value = true;
  nextTick(() => dialogElement.value?.focus());
}

function closeDialog() {
  dialogOpen.value = false;
  sending.value = false;
}

function submit() {
  if (sending.value) return;

  if (!canSubmit.value) {
    sent.value = false;
    error.value = 'Please select a topic and write a message.';
    return;
  }

  sending.value = true;
  sent.value = false;
  error.value = '';

  axios
    .post(
      config.support.endpoint,
      {
        system: props.current,
        category: category.value,
        message: message.value.trim(),
        url: window.location.href,
        path: window.location.pathname + window.location.search,
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        language: navigator.language,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
        submittedAt: new Date().toISOString(),
      },
      { withCredentials: true }
    )
    .then(() => {
      sent.value = true;
      category.value = '';
      message.value = '';
    })
    .catch((requestError) => {
      console.error('SystemSwitcher: failed to send support request', requestError);
      error.value = 'Your message could not be sent. Please try again in a moment.';
    })
    .finally(() => {
      sending.value = false;
    });
}

function onDocumentPointerDown(event) {
  if (helpOpen.value && helpRoot.value && !helpRoot.value.contains(event.target)) closeHelp();
}

function onDocumentKeydown(event) {
  if (event.key === 'Escape') {
    closeHelp();
    openKey.value = null;
    if (dialogOpen.value) closeDialog();
  }
}

onMounted(() => {
  document.addEventListener('mousedown', onDocumentPointerDown);
  document.addEventListener('keydown', onDocumentKeydown);

  axios
    .get('/oauth2/resources', { withCredentials: true })
    .then(({ data }) => {
      const map = {};
      (data ?? []).forEach((resource) => {
        if (resource?.id) map[resource.id] = resource;
      });
      access.value = map;
    })
    .catch((requestError) => {
      console.error('SystemSwitcher: failed to load resources', requestError);
    });
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocumentPointerDown);
  document.removeEventListener('keydown', onDocumentKeydown);
});
</script>

<style scoped lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Prompt:wght@400;600;700&display=swap');

$font: 'Prompt', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;

.system-switcher,
.system-switcher *:not(:where(svg, path, circle)) {
  all: revert;
  box-sizing: border-box;
  font-family: var(--sysnav-font, #{$font});
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  letter-spacing: normal;
  text-transform: none;
  color: var(--sysnav-primary);
}

.system-switcher {
  --sysnav-font: #{$font};
  --sysnav-primary: #1a2c51;
  --sysnav-primary-hover: #223a6b;
  --sysnav-secondary: #8d96a8;
  --sysnav-grey: #d6d6d6;
  --sysnav-light-grey: #e7e7e7;
  --sysnav-very-light-grey: #f5f5f5;
  --sysnav-white: #fff;
  --sysnav-red: #ff6565;
  --sysnav-green: #4c9a1f;
  --sysnav-bar-height: 34px;

  position: relative;
  z-index: 1000;
  width: 100%;
  background: var(--sysnav-light-grey);
  user-select: none;
  display: flex;
  align-items: stretch;
  flex-wrap: wrap;

  // Lift the whole bar so the modal overlay sits above page elements with a higher z-index.
  &.is-dialog-open {
    z-index: 99999999;
  }

  svg {
    display: block;
    flex: 0 0 auto;
  }

  .system-switcher-home {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
    height: var(--sysnav-bar-height);
    padding: 0 14px;
    color: var(--sysnav-primary);
    text-decoration: none;
    cursor: pointer;

    &:hover {
      background: var(--sysnav-grey);
    }
  }

  .system-switcher-item {
    position: relative;
    flex: 0 0 auto;

    &.is-active {
      background: var(--sysnav-very-light-grey);
    }

    .system-switcher-trigger {
      height: var(--sysnav-bar-height);
      padding: 0 14px;
      border: none;
      background: transparent;
      color: var(--sysnav-primary);
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 0.39px;
      line-height: 100%;
      text-transform: uppercase;
      white-space: nowrap;
      cursor: pointer;

      &:disabled {
        color: var(--sysnav-secondary);
        cursor: default;
      }
    }

    .system-switcher-dropdown {
      display: none;
      flex-direction: column;
      gap: 10px;
      position: absolute;
      top: 100%;
      left: 0;
      min-width: 100%;
      background: var(--sysnav-grey);
      box-shadow: 0 6px 14px rgba(26, 44, 81, 0.12);
      z-index: 9999999;
      padding: 10px;

      .system-switcher-dropdown-item {
        color: var(--sysnav-primary);
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.39px;
        text-transform: uppercase;
        padding-bottom: 12px;
        border-bottom: 1px solid var(--sysnav-secondary);
        text-decoration: none;
        white-space: nowrap;

        &:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }

        &:hover,
        &.is-active {
          color: var(--sysnav-secondary);
        }
      }
    }

    &:not(.is-disabled):hover > .system-switcher-trigger,
    &.is-open > .system-switcher-trigger {
      background: var(--sysnav-grey);
    }

    &:hover > .system-switcher-dropdown,
    &.is-open > .system-switcher-dropdown {
      display: flex;
    }
  }

  .help-menu {
    position: relative;
    margin-left: auto;
    flex: 0 0 auto;
  }

  .help-menu-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    height: var(--sysnav-bar-height);
    padding: 0 14px;
    border: none;
    background: transparent;
    color: var(--sysnav-primary);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.39px;
    line-height: 100%;
    text-transform: uppercase;
    white-space: nowrap;
    cursor: pointer;

    &:hover,
    &.is-open {
      background: var(--sysnav-grey);
    }
  }

  .help-menu-panel {
    position: absolute;
    top: 100%;
    right: 0;
    z-index: 9999999;
    width: 360px;
    max-width: calc(100vw - 20px);
    max-height: calc(100vh - 60px);
    overflow-y: auto;
    padding: 16px;
    background: var(--sysnav-white);
    box-shadow: 0 6px 14px rgba(26, 44, 81, 0.18);
    cursor: default;
  }

  .help-menu-title {
    margin: 0 0 12px;
    color: var(--sysnav-primary);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.39px;
    text-transform: uppercase;
  }

  .help-menu-manual {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 0;
    border-bottom: 1px solid var(--sysnav-light-grey);
    color: var(--sysnav-primary);
    text-decoration: none;

    &:hover {
      color: var(--sysnav-secondary);
    }

    svg {
      margin-top: 1px;
    }
  }

  .help-menu-manual-text {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .help-menu-manual-label {
    font-size: 14px;
    font-weight: 600;
    line-height: 130%;
  }

  .help-menu-manual-description {
    color: var(--sysnav-secondary);
    font-size: 13px;
    line-height: 140%;
  }

  .help-menu-contact {
    width: 100%;
    min-height: 40px;
    margin-top: 16px;
    padding: 10px 24px;
    border: none;
    background: var(--sysnav-primary);
    color: var(--sysnav-white);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.39px;
    text-transform: uppercase;
    cursor: pointer;

    &:hover {
      background: var(--sysnav-primary-hover);
    }
  }

  .help-dialog-overlay {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 99999999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    background: rgba(26, 44, 81, 0.3);
    cursor: default;
    // The bar sets user-select: none, which would otherwise block selecting and typing in the form.
    -webkit-user-select: text;
    user-select: text;
  }

  .help-dialog {
    width: 562px;
    max-width: 100%;
    max-height: 100%;
    overflow-y: auto;
    border-radius: 8px;
    background: var(--sysnav-white);
    box-shadow: 0 0 30px rgba(0, 0, 0, 0.1);
    color: var(--sysnav-primary);

    &:focus {
      outline: none;
    }

    .help-dialog-form {
      display: flex;
      flex-direction: column;
      gap: 44px;
      padding: 21.5px 22.5px 22.5px;
    }

    .help-dialog-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 16px;
    }

    .help-dialog-heading {
      margin: 0;
      font-size: 17px;
      font-weight: 700;
      line-height: 19px;
      text-transform: uppercase;
    }

    .help-dialog-close {
      display: flex;
      flex: 0 0 auto;
      padding: 0;
      border: none;
      background: transparent;
      color: var(--sysnav-primary);
      cursor: pointer;

      &:hover {
        color: var(--sysnav-secondary);
      }
    }

    .help-dialog-fields {
      display: flex;
      flex-direction: column;
      gap: 22.5px;
    }

    .help-dialog-field {
      display: flex;
      flex-direction: column;
      gap: 5px;
    }

    .help-dialog-label {
      color: var(--sysnav-secondary);
      font-size: 14px;
      letter-spacing: 0.035em;
    }

    .help-dialog-select {
      position: relative;

      svg {
        position: absolute;
        top: 50%;
        right: 15.5px;
        transform: translateY(-50%);
        color: var(--sysnav-primary);
        pointer-events: none;
      }
    }

    .help-dialog-input {
      width: 100%;
      margin: 0;
      border: 1px solid #d1d1d1;
      border-radius: 4px;
      background: var(--sysnav-white);
      color: #000;
      font-size: 16px;
      letter-spacing: 0.02em;

      &::placeholder {
        color: var(--sysnav-secondary);
        opacity: 1;
      }

      &:focus {
        outline: none;
        border-color: var(--sysnav-primary);
      }

      &:disabled {
        background: var(--sysnav-light-grey);
      }
    }

    select.help-dialog-input {
      height: 44px;
      padding: 0 46px 0 15.5px;
      appearance: none;
      cursor: pointer;
    }

    .help-dialog-textarea {
      height: 161px;
      padding: 10px;
      resize: none;
    }

    .help-dialog-status {
      margin: 0;
      font-size: 14px;

      &.is-error {
        color: var(--sysnav-red);
      }

      &.is-success {
        color: var(--sysnav-green);
      }
    }

    .help-dialog-actions {
      display: flex;
      justify-content: center;
      gap: 22.5px;
    }

    .help-dialog-cancel,
    .help-dialog-submit {
      display: flex;
      flex: 0 1 135px;
      align-items: center;
      justify-content: center;
      height: 44px;
      padding: 0;
      border: 1px solid var(--sysnav-primary);
      border-radius: 4px;
      font-size: 17px;
      font-weight: 700;
      line-height: 1;
      text-transform: uppercase;
      cursor: pointer;

      &:hover:not(:disabled) {
        opacity: 0.7;
      }

      &:disabled {
        cursor: not-allowed;
      }
    }

    .help-dialog-cancel {
      background: var(--sysnav-white);
      color: var(--sysnav-primary);
    }

    .help-dialog-submit {
      background: var(--sysnav-primary);
      color: var(--sysnav-white);
    }
  }
}
</style>
